import React, { useState } from 'react'
import { CreateRebateSettings, RebateSettingsDto } from '@/api/Trading-Accounts/RebateSettingsDto'
import ClientAreaAuditLogModal from '@/components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import { FormField, FormSelectField } from '@/components/Form/FormField'
import { LoadingOverlay } from '@/components/LoadingOverlay/LoadingOverlay'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { TickmillCompanyTypes } from 'api/Clients/Types/TickmillCompanyTypes'
import { TickmillCompanyDto } from 'api/Identity/Types/Company'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { FormSubmitValues } from 'utils/formValidation'

export interface RebateSettingsFormValues {
  tickmillCompanyId?: number
  automationCapInUsd?: number
}

interface OuterProps {
  isOpen?: boolean
  data: RebateSettingsDto | undefined
  tickmillCompanies: TickmillCompanyDto[]
  isEdit?: boolean
  onClose(): void
  onSubmit(values: FormSubmitValues<CreateRebateSettings>): Promise<void>
}

type RebateSettingsFormProps = FormikProps<RebateSettingsFormValues> & OuterProps

const RebateSettingsFormUI: React.FC<RebateSettingsFormProps> = (props) => {
  const { handleSubmit, data, errors, touched, tickmillCompanies, isEdit, isSubmitting } = props
  const [auditLogVisible, setAuditLogVisible] = useState(false)

  const handleSubmitForm = () => {
    handleSubmit()
  }

  return (
    <Dialog open={props.isOpen} onOpenChange={(open) => !open && props.onClose()}>
      <ClientAreaAuditLogModal
        isVisible={auditLogVisible}
        onClose={() => setAuditLogVisible(false)}
        rebateSettingsId={data?.id}
      />
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Rebate Settings' : 'New Rebate Settings'}</DialogTitle>
        </DialogHeader>
        <Form>
          <div className='flex flex-col gap-4'>
            <FormSelectField
              name='tickmillCompanyId'
              label='IB Entity'
              options={tickmillCompanies.map((company) => ({
                id: company.id,
                name: company.name ?? '',
              }))}
              showSearch
              allowClear
              disabled={isEdit}
              error={touched.tickmillCompanyId ? errors.tickmillCompanyId : undefined}
              onChange={(value) =>
                props.setFieldValue('tickmillCompanyId', value ? Number(value) : undefined)
              }
            />
            <FormField
              name='automationCapInUsd'
              label='Automation Cap'
              type='number'
              error={touched.automationCapInUsd ? errors.automationCapInUsd : undefined}
            />
          </div>
        </Form>
        <DialogFooter>
          <Button variant='outline' key='audit-log' onClick={() => setAuditLogVisible(true)}>
            Audit Log
          </Button>
          <Button key='button' type='button' onClick={handleSubmitForm} disabled={!props.isValid}>
            Save
          </Button>
        </DialogFooter>
        <LoadingOverlay isLoading={isSubmitting} />
      </DialogContent>
    </Dialog>
  )
}

export const RebateSettingsForm = withFormik<OuterProps, RebateSettingsFormValues>({
  mapPropsToValues: (props) => {
    const { data } = props
    const defaultCompany = getDefaultCompanyId(props.tickmillCompanies)

    return {
      tickmillCompanyId: data?.ibEntity?.id ?? defaultCompany,
      automationCapInUsd: data?.automationCapInUsd ?? 0,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)
      const data = dataToSubmit(values)
      await props.onSubmit(data)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values, props) => {
    const errors: FormikErrors<RebateSettingsFormValues> = {}

    if (!values.tickmillCompanyId && !props.isEdit) {
      errors.tickmillCompanyId = 'Required'
    }

    if (!values.automationCapInUsd) {
      errors.automationCapInUsd = 'Required'
    }

    return errors
  },
  enableReinitialize: false,
  validateOnChange: true,
})(RebateSettingsFormUI)

const dataToSubmit = (values: RebateSettingsFormValues) => {
  return {
    tickmillCompanyId: values.tickmillCompanyId || 0,
    automationCapInUsd: values.automationCapInUsd || 0,
  }
}

const getDefaultCompanyId = (companies: TickmillCompanyDto[]): TickmillCompanyTypes | undefined => {
  return companies.find((x) => x.id === TickmillCompanyTypes.Tickmill_SC_Company)?.id
}
